import type { WritePreviewChange } from '@/lib/types/chat';

/**
 * Pure helpers for the Ask Coffee approval card. The agent sends machine values only
 * (tool names, mutation variable names, raw values); everything shown is resolved here
 * through the i18n catalogs, so the card is localized like the rest of the app.
 */

export const APPROVAL_ACTIONS = ['create', 'update', 'delete', 'complete', 'add', 'remove', 'run'] as const;
export type ApprovalAction = (typeof APPROVAL_ACTIONS)[number];

/** Rows shown before "and N more". */
export const MAX_VISIBLE_ITEMS = 10;

/** `createDeal` -> {action: 'create', entity: 'deal'}; `addRecordsToList` -> entity `recordstolist`. */
export function splitTool(tool: string): { action: ApprovalAction | null; entity: string } {
  for (const action of APPROVAL_ACTIONS) {
    if (tool.startsWith(action) && tool.length > action.length) {
      return { action, entity: tool.slice(action.length).toLowerCase() };
    }
  }
  return { action: null, entity: tool.toLowerCase() };
}

// Mutation variable -> catalog key, per entity first, then for any entity.
const ENTITY_FIELD_KEYS: Record<string, Record<string, string>> = {
  deal: {
    amount: 'fields.deals.amount',
    closeDate: 'fields.deals.closeDate',
    stage: 'fields.deals.stage',
    NextStep: 'fields.deals.nextStep',
    forecastCategory: 'fields.deals.forecastCategory',
    contactIds: 'fields.deals.contacts',
    accountIds: 'fields.deals.company',
  },
  contact: {
    firstName: 'fields.people.firstName',
    lastName: 'fields.people.lastName',
    jobTitle: 'fields.people.title',
    accountIds: 'fields.people.company',
  },
  account: {
    employeeCount: 'fields.companies.employees',
    annualRevenue: 'fields.companies.revenue',
    companySizeCategory: 'fields.companies.sizeCategory',
    foundedYear: 'fields.companies.foundingYear',
    postalCode: 'fields.companies.postalCode',
    entityType: 'fields.companies.companyType',
    raisedAmount: 'fields.companies.funding',
    techTags: 'fields.companies.technologies',
    state: 'fields.companies.stateProvince',
  },
};

const COMMON_FIELD_KEYS: Record<string, string> = {
  name: 'common.name',
  description: 'common.description',
  phone: 'common.phone',
  email: 'common.email',
  primaryEmail: 'common.email',
  city: 'common.city',
  country: 'common.country',
  state: 'websiteForms.field.state',
  industry: 'common.industry',
  domain: 'common.website',
  priority: 'common.priority',
  status: 'common.status',
  title: 'common.title',
  type: 'common.type',
  note: 'common.note',
  dueDate: 'tasks.dueDate',
  probability: 'deals.probability',
  ownerId: 'fields.deals.owner',
  blocks: 'forms.note.content',
  options: 'forms.customField.options',
  recordIds: 'askCoffee.records',
  public: 'notes.public',
  highlight: 'askCoffee.approvalField.highlight',
  additionalEmails: 'askCoffee.approvalField.additionalEmails',
  additionalDomains: 'askCoffee.approvalField.additionalDomains',
  aboutUs: 'askCoffee.approvalField.aboutUs',
  revenueRange: 'askCoffee.approvalField.revenueRange',
  revenueMin: 'askCoffee.approvalField.revenueMin',
  revenueMax: 'askCoffee.approvalField.revenueMax',
  listId: 'askCoffee.approvalField.list',
  crmLabel: 'common.name',
  modelType: 'common.type',
};

// Literal keys, not built from the action/entity: the unused-key scan needs the literal.
const ACTION_KEYS: Record<ApprovalAction, string> = {
  create: 'askCoffee.approvalAction.create',
  update: 'askCoffee.approvalAction.update',
  delete: 'askCoffee.approvalAction.delete',
  complete: 'askCoffee.approvalAction.complete',
  add: 'askCoffee.approvalAction.add',
  remove: 'askCoffee.approvalAction.remove',
  run: 'askCoffee.approvalAction.run',
};

const ENTITY_KEYS: Record<string, string> = {
  deal: 'askCoffee.approvalEntity.deal',
  contact: 'askCoffee.approvalEntity.contact',
  account: 'askCoffee.approvalEntity.account',
  task: 'askCoffee.approvalEntity.task',
  note: 'askCoffee.approvalEntity.note',
  list: 'askCoffee.approvalEntity.list',
  recordstolist: 'askCoffee.approvalEntity.list',
  recordsfromlist: 'askCoffee.approvalEntity.list',
  customfield: 'askCoffee.approvalEntity.customField',
  report: 'askCoffee.approvalEntity.report',
};

/** Catalog key for an action, or null for a verb the card does not know. */
export function actionLabelKey(action: ApprovalAction | null): string | null {
  return action ? ACTION_KEYS[action] : null;
}

/** Catalog key for an entity, or null for one the card does not know. */
export function entityLabelKey(entity: string): string | null {
  return ENTITY_KEYS[entity] ?? null;
}

// Task enum values as the backend stores them -> catalog keys. Deal priority is free text
// ("High"), so this only applies to tasks and notes.
const TASK_VALUE_KEYS: Record<string, Record<string, string>> = {
  status: { TODO: 'askCoffee.approvalValue.todo', COMPLETED: 'tasks.completed' },
  priority: { LOW: 'common.low', MEDIUM: 'common.medium', HIGH: 'common.high' },
};

/** Catalog key for a known enum value, or null to show the value as it is. */
export function valueLabelKey(entity: string, field: string, value: unknown): string | null {
  if (entity !== 'task' && entity !== 'note') return null;
  return TASK_VALUE_KEYS[field]?.[String(value).toUpperCase()] ?? null;
}

const DATE_FIELDS = new Set(['dueDate', 'closeDate']);

/**
 * A date field's value as {date, withTime}, or null when the field is not a date or the
 * value does not parse. Midnight means the user set a day, not a time.
 */
export function dateValue(field: string, value: unknown): { date: Date; withTime: boolean } | null {
  if (!DATE_FIELDS.has(field) || typeof value !== 'string') return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return { date, withTime: !/T00:00(:00(\.0+)?)?(Z|[+-]00:?00)?$/.test(value) && value.includes('T') };
}

/** Every catalog key this module can return, for the catalog test. */
export const APPROVAL_CATALOG_KEYS: string[] = [
  ...Object.values(ENTITY_FIELD_KEYS).flatMap((m) => Object.values(m)),
  ...Object.values(COMMON_FIELD_KEYS),
  ...Object.values(ACTION_KEYS),
  ...Object.values(ENTITY_KEYS),
  ...Object.values(TASK_VALUE_KEYS).flatMap((m) => Object.values(m)),
];

/** Catalog key for a standard field, or null for a workspace custom field. */
export function fieldLabelKey(entity: string, field: string): string | null {
  return ENTITY_FIELD_KEYS[entity]?.[field] ?? COMMON_FIELD_KEYS[field] ?? null;
}

/**
 * A custom field's crmName (`sales_region_1234_coffee`) shown as the workspace named it
 * (`sales region`). The name is the workspace's own data, not app copy.
 */
export function customFieldName(field: string): string {
  return field.replace(/_\d+_coffee$/, '').replace(/_/g, ' ');
}

/** No `before` key means the card shows only the new value ("set to"). */
export function hasBefore(change: WritePreviewChange): boolean {
  return Object.prototype.hasOwnProperty.call(change, 'before');
}

/** A field that changes with no displayable value, e.g. a note's body. */
export function isOpaque(change: WritePreviewChange): boolean {
  return !hasBefore(change) && !Object.prototype.hasOwnProperty.call(change, 'after');
}

/** A value as text, or null when it is empty (the card says so in the user's language). */
export function formatValue(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) {
    const parts = value.map(formatValue).filter((p): p is string => p !== null);
    return parts.length ? parts.join(', ') : null;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  const text = String(value);
  // Backend dates arrive as ISO timestamps; the date part is what the user set.
  const isoDate = /^(\d{4}-\d{2}-\d{2})T00:00:00/.exec(text);
  return isoDate ? isoDate[1] : text;
}
